/**
 * Слои вкладки «Голоса клиента» (#2060, #2072, спека
 * docs/plans/2026-10-11-voice-report-layers.md §2.1–2.2): сводка из 2–4
 * карточек и переключатель разделов под ней — одни для «Продаж» и «Сайта и
 * контента». Вкладка решает, какие карточки и разделы у неё есть, и рисует
 * содержимое разделов (`TabsContent`) детьми.
 *
 * Сетка сводки считается по ширине области, а не окна (`@container`): в
 * портале сайдбар съедает 256 px, во встроенном Bitrix24 его нет. Карточка в
 * ряду не уже ~240 px; нечётная последняя в два столбца растягивается на оба,
 * чтобы правая половина ряда не стояла пустой (#2081).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VoiceSummaryCard } from './VoiceSummaryCard';
import type { VoiceSectionId } from './voice-search';
import type { SummaryCardState } from './voice-summary-view-model';

export interface VoiceLayerCard<S extends VoiceSectionId> {
  /** The section the card opens. */
  id: S;
  title: string;
  state: SummaryCardState;
}

/** Columns by the summary's own width: two from 576 px, all in a row once each card gets ~240 px. */
const SUMMARY_GRID: Record<number, string> = {
  2: '@xl:grid-cols-2',
  3: '@xl:grid-cols-2 @3xl:grid-cols-3',
  4: '@xl:grid-cols-2 @5xl:grid-cols-4',
};
/** An odd last card in two columns spans both, then takes its own column in a row of three. */
const ODD_LAST_CARD = '@xl:col-span-2 @3xl:col-span-1';

interface VoiceLayersProps<S extends VoiceSectionId> {
  cards: VoiceLayerCard<S>[];
  /** The tab's sections, in order; the first is the default. */
  sections: readonly S[];
  /** The open one — one of `sections`. */
  section: S;
  onOpen: (id: S) => void;
  /** The sections' `TabsContent`. */
  children: ReactNode;
}

export function VoiceLayers<S extends VoiceSectionId>({
  cards,
  sections,
  section,
  onOpen,
  children,
}: VoiceLayersProps<S>) {
  const { t } = useTranslation('dashboard');
  const oddLast = cards.length > 1 && cards.length % 2 === 1;
  return (
    <>
      <div className="@container">
        <div
          className={cn('grid grid-cols-1 gap-3', SUMMARY_GRID[cards.length])}
          data-testid="voice-summary"
        >
          {cards.map((card, index) => (
            <VoiceSummaryCard
              key={card.id}
              title={card.title}
              state={card.state}
              active={section === card.id}
              onOpen={() => onOpen(card.id)}
              className={
                oddLast && index === cards.length - 1 ? ODD_LAST_CARD : undefined
              }
            />
          ))}
        </div>
      </div>

      <Tabs
        value={section}
        onValueChange={(value) => {
          const next = sections.find((id) => id === value);
          if (next) onOpen(next);
        }}
        className="space-y-4"
      >
        {/* Wraps on a narrow frame (§5): four long labels on Site. */}
        <TabsList
          aria-label={t('voicePage.layers.sectionsLabel')}
          className="h-auto flex-wrap justify-start"
        >
          {sections.map((id) => (
            <TabsTrigger key={id} value={id}>
              {t(`voicePage.layers.section.${id}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {children}
      </Tabs>
    </>
  );
}
